/**
 * One file series dataset: its tables and the upload that adds them
 * (specs/file-series-datasets.md, #1562). A tidy sheet goes to the bucket by
 * the presigned path and is parsed on completion; a failed parse names the
 * sheet, row and column.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { ArrowLeft, FileUp, Trash2, Download } from 'lucide-react';
import API_ENDPOINTS from '../../../config/api';
import { Button, Actions, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { useConfirm } from '../../../components/DialogProvider';
import { getDataset, listSeriesTables, uploadSeriesFiles, deleteSeriesTable } from './datasetApi';
import { Section, SectionTitle, Lead, Table, TableScroll, ErrorText } from './styles';

const DropZone = styled.label`
  display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 24px; border-radius: 12px; cursor: pointer;
  border: 2px dashed ${(p) => (p.$over ? '#c9a7ff' : '#2c3444')}; color: rgba(255,255,255,0.75); text-align: center;
  input { display: none; }
`;
const Picked = styled.span`font-size: 13px; color: #c9a7ff;`;
const Small = styled.span`font-size: 12px; color: rgba(255,255,255,0.55);`;

const accepted = (f) => /\.(xlsx|csv)$/i.test(f.name);

export default function SeriesDatasetView({ datasetId, onBack }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const inputRef = useRef(null);
  const [dataset, setDataset] = useState(null);
  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, ts] = await Promise.all([getDataset(datasetId), listSeriesTables(datasetId)]);
      setDataset(d.dataset || d);
      setTables(ts);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [datasetId]);
  useEffect(() => { load(); }, [load]);

  const pick = (list) => { setFiles([...(list || [])].filter(accepted)); setMessage(null); };

  const upload = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const r = await uploadSeriesFiles(datasetId, files, {
        onProgress: ({ loaded, total }) => setProgress(Math.round((loaded / total) * 100)),
      });
      setFiles([]);
      if (inputRef.current) inputRef.current.value = '';
      const failed = (r.failed || []).map((f) => `${f.name}: ${f.error}`);
      setMessage({ ok: !failed.length, text: [t('knowledgeKinds.series.added', { count: r.added?.length || 0 }), ...failed].join(' ') });
      await load();
    } catch (err) {
      setMessage({ ok: false, text: err.body?.failed?.map((f) => `${f.name}: ${f.error}`).join('; ') || err.message });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const remove = async (table) => {
    if (!(await confirm({ message: t('knowledgeKinds.series.removeConfirm', { title: table.title }), destructive: true }))) return;
    try {
      await deleteSeriesTable(datasetId, table.id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <Actions>
        <Button type="button" $variant="ghost" onClick={onBack}><ArrowLeft /> {t('knowledgeKinds.series.back')}</Button>
      </Actions>
      {error && <ErrorText>{error}</ErrorText>}
      <Section>
        <SectionTitle>{dataset?.name || datasetId} · {t('knowledgeKinds.series.tables')}</SectionTitle>
        {!tables.length && <Lead>{t('knowledgeKinds.series.noTables')}</Lead>}
        {tables.length > 0 && (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>{t('knowledgeKinds.series.tables')}</th>
                  <th>{t('knowledgeKinds.series.dimensions')}</th>
                  <th>{t('knowledgeKinds.series.periods')}</th>
                  <th>{t('knowledgeKinds.series.rows')}</th>
                  <th>{t('knowledgeKinds.series.source')}</th>
                  <th>{t('knowledgeKinds.series.imported')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tables.map((x) => (
                  <tr key={x.id}>
                    <td><strong>{x.title}</strong><br /><Small>{x.key}{x.unit ? ` · ${x.unit}` : ''}</Small></td>
                    <td>{(x.dimensions || []).map((d) => `${d.label} (${d.values.length})`).join(', ')}</td>
                    <td>{x.periods?.length ? `${x.periods[0]}–${x.periods[x.periods.length - 1]} (${x.periods.length})` : ''}</td>
                    <td>{x.row_count}</td>
                    <td>{x.source}{x.licence ? <><br /><Small>{x.licence}</Small></> : null}</td>
                    <td>{x.imported_at ? new Date(x.imported_at).toLocaleDateString('fi-FI') : ''}{x.filename ? <><br /><Small>{x.filename}</Small></> : null}</td>
                    <td>
                      <Actions>
                        {x.filename && (
                          <Button as="a" $size="small" href={API_ENDPOINTS.ADMIN_DATASET_SERIES_TABLE_FILE(datasetId, x.id)}><Download size={14} /> {t('knowledgeKinds.series.file')}</Button>
                        )}
                        <Button type="button" $size="small" $variant="danger" onClick={() => remove(x)}><Trash2 size={14} /> {t('knowledgeKinds.series.remove')}</Button>
                      </Actions>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </Section>
      <Section>
        <SectionTitle><FileUp size={18} /> {t('knowledgeKinds.series.uploadTitle')}</SectionTitle>
        <Lead>{t('knowledgeKinds.series.uploadLead')}</Lead>
        <DropZone
          $over={over}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files); }}
        >
          <FileUp size={22} />
          {t('knowledgeKinds.series.drop')}
          <input ref={inputRef} type="file" multiple accept=".xlsx,.csv" onChange={(e) => pick(e.target.files)} />
          {files.length > 0 && <Picked>{t('knowledgeKinds.series.picked', { count: files.length })}</Picked>}
        </DropZone>
        <Actions>
          <Button type="button" $variant="primary" disabled={busy || !files.length} onClick={upload}>
            {t('knowledgeKinds.series.upload')}{progress != null ? ` ${progress}%` : ''}
          </Button>
          {message && <StatusLine $error={!message.ok}>{message.text}</StatusLine>}
        </Actions>
      </Section>
    </>
  );
}
